import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import { ArchiveBoxArrowDownIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { IPart } from "../../models/Part";
import { IPartBin } from "../../models/PartBin";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import LoadingWheel from "../shared/LoadingWheel";

interface AllocatePartsToBinModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  /** Every bin the user can allocate into. */
  bins: IPartBin[];
  binsLoading?: boolean;
  /** The bin currently chosen, either from the combobox or from a bin scan. */
  selectedBinId: string | null;
  onSelectBin: (binId: string | null) => void;
  /** The job's parts, in the order they're shown on the page. */
  parts: IPart[];
  /**
   * Name of the assembly the parts have been narrowed to, when the workspace is restricted to one
   * (Station Manager). Only used to say so on screen - the caller does the filtering.
   */
  scopeName?: string | null;
  selectedPartIds: Set<string>;
  onTogglePart: (partId: string) => void;
  onToggleAllParts: () => void;
  onAllocate: () => void;
  isAllocating: boolean;
}

// Allocating a job's parts into a bin, either by picking a bin and ticking parts or entirely by
// scanner: scanning a bin label opens this with that bin selected, scanning part labels ticks them,
// and scanning the bin again allocates (the scan handling lives in the calling page, which owns the
// scanner override).
export default function AllocatePartsToBinModal({
  open,
  setOpen,
  bins,
  binsLoading = false,
  selectedBinId,
  onSelectBin,
  parts,
  scopeName,
  selectedPartIds,
  onTogglePart,
  onToggleAllParts,
  onAllocate,
  isAllocating,
}: AllocatePartsToBinModalProps) {
  const { t } = useTranslation("jobs");

  const selectedBin = bins.find((bin) => bin.id === selectedBinId);
  const allSelected = parts.length > 0 && parts.every((part) => selectedPartIds.has(part.id));

  const binName = (partBinId: string | null | undefined) =>
    partBinId ? bins.find((bin) => bin.id === partBinId)?.name ?? t("Another bin") : null;

  return (
    <Modal open={open} setOpen={setOpen} width={"max-w-2xl"}>
      <div className="text-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">{t("Allocate Parts to Bin")}</h3>
        <p className="text-sm text-gray-600 mt-2">
          {t("Pick a bin and choose the parts going into it, or scan the bin and then each part.")}
        </p>
        {scopeName && (
          <p className="text-xs text-gray-400 mt-1">
            {t("Showing {{name}} parts only.", { name: scopeName })}
          </p>
        )}
      </div>

      {isAllocating ? (
        <div className="py-10">
          <LoadingWheel />
        </div>
      ) : (
        <>
          <div className="mt-4">
            <span className="text-sm font-medium text-gray-500">{t("Bin")}</span>
            <ComboBoxInput
              // ComboBoxInput only reads initialValue on mount, so remount it when a scan picks
              // the bin for us - and again once the bins arrive, since a scan can open this
              // before the list has loaded and there'd be nothing to match against yet.
              key={`allocate-bin-${selectedBinId ?? "none"}-${bins.length}`}
              options={bins}
              searchField={"name"}
              valueField={"id"}
              register={null}
              registerName={"partBinId"}
              setValue={(_name: string, value: string) => onSelectBin(value ?? null)}
              initialValue={selectedBinId ?? ""}
              placeholder={binsLoading ? t("Loading bins...") : t("Search for a bin")}
              disabled={binsLoading}
              containerClasses="w-full"
            />
          </div>

          {selectedBin && (
            <div className="mt-4 flex items-center gap-4 rounded-md bg-gray-50 p-3">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(`bin|${selectedBin.id}|${selectedBin.name ?? "NULL"}`)}`}
                alt={t("Bin QR code")}
                className="h-24 w-24 rounded-md bg-white"
              />
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">{selectedBin.name}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {t("Scan parts to select them, then scan this bin again to allocate.")}
                </p>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">
              {t("Parts ({{selected}} of {{total}} selected)", {
                selected: selectedPartIds.size,
                total: parts.length,
              })}
            </span>
            {parts.length > 0 && (
              <button
                type="button"
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
                onClick={onToggleAllParts}
              >
                {allSelected ? t("Clear all") : t("Select all")}
              </button>
            )}
          </div>

          <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100">
            {parts.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 text-center">
                {scopeName
                  ? t("No parts at {{name}} to allocate.", { name: scopeName })
                  : t("No parts on this job to allocate.")}
              </p>
            ) : (
              parts.map((part) => {
                const currentBin = binName(part.partBinId);
                const isSelected = selectedPartIds.has(part.id);

                return (
                  <label
                    key={part.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${isSelected ? "bg-primary-50" : "hover:bg-gray-50"}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onTogglePart(part.id)}
                      className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {part.title ?? t("Part")}
                      </span>
                      {part.tagNumber && (
                        <span className="block text-xs text-gray-500">{part.tagNumber}</span>
                      )}
                    </span>
                    {currentBin && (
                      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                        {t("In {{name}}", { name: currentBin })}
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              className="inline-flex flex-1 items-center justify-center gap-x-2 rounded-md border border-transparent shadow-sm px-4 py-3 text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onAllocate}
              disabled={!selectedBinId || selectedPartIds.size === 0}
            >
              <ArchiveBoxArrowDownIcon className="h-5 w-5" aria-hidden="true" />
              {t("Allocate ({{count}})", { count: selectedPartIds.size })}
            </button>
            <button
              type="button"
              className="inline-flex flex-1 justify-center rounded-md border border-gray-300 px-4 py-3 bg-white text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500"
              onClick={() => setOpen(false)}
            >
              {t("Cancel")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
